import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { marketplaceService } from '../../services/marketplaceService';
import { getCachedIcon } from '../../services/iconCache';
import {
  PricingConflictError,
  hasPrice,
  matchesLicenceQuery,
  parsePrice,
  sameNamedPricedLicences,
  toPartnerPriceDraft,
  toTermPrices,
  type MicrosoftLicence,
  type MicrosoftLicenceReference,
  type PartnerPriceDraft,
  type TermPriceText,
  type TermPrices,
} from '../microsoftLicencePricing';

const PAGE_SIZE = 100;

function pricedCount(list: Record<string, TermPriceText> | undefined): number {
  return Object.values(list ?? {}).filter(hasPrice).length;
}

function hasEnteredPrice(text: TermPriceText | undefined): boolean {
  return Object.values(text ?? {}).some((value) => value.trim() !== '');
}

function withTelstraFirst<T extends { id: string }>(distributors: readonly T[]): T[] {
  return [...distributors].sort((left, right) => Number(right.id === 'telstra') - Number(left.id === 'telstra'));
}

function invalidCount(list: Record<string, TermPriceText> | undefined): number {
  return Object.values(list ?? {}).reduce((sum, text) => sum + toTermPrices(text).invalid.length, 0);
}

export function MicrosoftLicencesTab() {
  const [reference, setReference] = useState<MicrosoftLicenceReference | null>(null);
  const [version, setVersion] = useState<string | null>(null);
  const [defaultDistributor, setDefaultDistributor] = useState('');
  const [draft, setDraft] = useState<PartnerPriceDraft>({});
  const [retired, setRetired] = useState<string[]>([]);
  const [activeDistributor, setActiveDistributor] = useState('');
  const [query, setQuery] = useState('');
  const [storefrontOnly, setStorefrontOnly] = useState(true);
  const [pricedOnly, setPricedOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [descriptions, setDescriptions] = useState<Record<string, string>>({});
  const [openLicenceId, setOpenLicenceId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setSaveError(null);
    setConflict(false);
    setMessage(null);
    try {
      const [loadedReference, pricing] = await Promise.all([
        marketplaceService.getMicrosoftLicenceReference(),
        marketplaceService.getPartnerMicrosoftLicencePricing(),
      ]);
      const loaded = toPartnerPriceDraft(loadedReference, pricing);
      setReference(loadedReference);
      setVersion(pricing.version);
      setDefaultDistributor(pricing.defaultDistributor ?? '');
      setDraft(loaded.draft);
      setDescriptions(pricing.descriptions ?? {});
      setRetired(loaded.retired);
      setActiveDistributor((current) => current || pricing.defaultDistributor || loadedReference.distributors[0]?.id || '');
      setDirty(loaded.retired.length > 0);
    } catch (err: unknown) {
      setReference(null);
      setLoadError(err instanceof Error ? err.message : "Couldn't load your Microsoft licence prices");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(0);
  }, [query, storefrontOnly, pricedOnly, activeDistributor]);

  const seatLicences = useMemo(
    () => (reference?.licences ?? []).filter((licence) => licence.kind === 'seat'),
    [reference],
  );

  const visible = useMemo(
    () => seatLicences.filter((licence) =>
      (!storefrontOnly || licence.storefront)
      && (!pricedOnly || hasEnteredPrice(draft[activeDistributor]?.[licence.skuId]))
      && matchesLicenceQuery(licence, query)),
    [seatLicences, storefrontOnly, pricedOnly, draft, activeDistributor, query],
  );

  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageStart = currentPage * PAGE_SIZE;
  const pageRows = visible.slice(pageStart, pageStart + PAGE_SIZE);

  const sameNamedIn = useCallback(
    (list: Record<string, TermPriceText> | undefined) => sameNamedPricedLicences(seatLicences, (skuId) => hasPrice(list?.[skuId])),
    [seatLicences],
  );

  const activeSameNamed = useMemo(() => sameNamedIn(draft[activeDistributor]), [sameNamedIn, draft, activeDistributor]);

  const activeSameNames = useMemo(
    () => Array.from(new Set(seatLicences.filter((licence) => activeSameNamed.has(licence.skuId)).map((licence) => licence.name))),
    [seatLicences, activeSameNamed],
  );

  const activeList = draft[activeDistributor] ?? {};

  const updatePrice = (skuId: string, term: string, text: string) => {
    setDraft((current) => ({
      ...current,
      [activeDistributor]: {
        ...current[activeDistributor],
        [skuId]: { ...current[activeDistributor]?.[skuId], [term]: text },
      },
    }));
    setDirty(true);
    setMessage(null);
  };

  const save = async () => {
    if (!reference) return;
    setSaveError(null);
    setConflict(false);
    setMessage(null);

    if (!defaultDistributor) {
      setSaveError('Choose the distributor your customers use by default.');
      return;
    }
    const invalid = reference.distributors
      .map((distributor) => ({ label: distributor.label, count: invalidCount(draft[distributor.id]) }))
      .filter((entry) => entry.count > 0);
    if (invalid.length > 0) {
      setSaveError(`Some prices aren't valid amounts (${invalid.map((entry) => `${entry.label}: ${entry.count}`).join(', ')}). Use numbers like 34.20 and fix the boxes marked in red.`);
      return;
    }

    const distributors: Record<string, { prices: Record<string, TermPrices> }> = {};
    for (const distributor of reference.distributors) {
      const prices = Object.fromEntries(
        Object.entries(draft[distributor.id] ?? {})
          .map(([skuId, text]): [string, TermPrices] => [skuId, toTermPrices(text).prices])
          .filter(([, termPrices]) => Object.keys(termPrices).length > 0),
      );
      if (Object.keys(prices).length > 0) distributors[distributor.id] = { prices };
    }

    setSaving(true);
    try {
      const saved = await marketplaceService.savePartnerMicrosoftLicencePricing({
        version,
        defaultDistributor,
        distributors,
        descriptions,
      });
      const next = toPartnerPriceDraft(reference, saved);
      setVersion(saved.version);
      setDefaultDistributor(saved.defaultDistributor ?? '');
      setDraft(next.draft);
      setDescriptions(saved.descriptions ?? {});
      setRetired(next.retired);
      setDirty(false);
      setMessage('Saved. Customers see the new prices on their next marketplace refresh.');
    } catch (err: unknown) {
      setConflict(err instanceof PricingConflictError);
      setSaveError(err instanceof Error ? err.message : "Couldn't save your Microsoft licence prices");
    } finally {
      setSaving(false);
    }
  };

  const activeLabel = reference?.distributors.find((distributor) => distributor.id === activeDistributor)?.label ?? '';
  const openLicence = seatLicences.find((licence) => licence.skuId === openLicenceId) ?? null;

  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <div className="border-b border-slate-200 px-6 py-4">
        <h2 className="text-lg font-semibold text-slate-900">Microsoft licences</h2>
        <p className="mt-1 max-w-3xl text-sm text-slate-500">
          Every customer&apos;s catalogue includes these licences, with Microsoft&apos;s SKU, name, image and contract terms
          already attached. Keep a price list for each distributor you buy through. Customers use your default distributor
          unless you choose another on their marketplace page, where you can also set a price for just that customer.
          Customers only see a licence once it has a price.
        </p>

        {loadError && (
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            <span>{loadError}</span>
            <button
              type="button"
              onClick={() => void load()}
              className="rounded-md border border-rose-300 bg-white px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100"
            >
              Try again
            </button>
          </div>
        )}

        {reference && (
          <>
            <div className="mt-4 flex flex-wrap items-end gap-4">
              <label className="flex flex-col gap-1 text-xs font-medium text-slate-700">
                Default distributor
                <select
                  value={defaultDistributor}
                  onChange={(event) => {
                    setDefaultDistributor(event.target.value);
                    setDirty(true);
                    setMessage(null);
                  }}
                  className={`w-56 rounded-lg border px-3 py-2 text-sm text-slate-900 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 ${
                    defaultDistributor ? 'border-slate-300' : 'border-amber-400'
                  }`}
                  aria-label="Default distributor"
                >
                  <option value="">Choose a distributor</option>
                  {reference.distributors.map((distributor) => (
                    <option key={distributor.id} value={distributor.id}>{distributor.label}</option>
                  ))}
                </select>
              </label>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by name or String ID, e.g. SPB"
                className="w-80 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
              />
              <label className="flex items-center gap-2 py-2 text-sm text-slate-700">
                <input type="checkbox" checked={storefrontOnly} onChange={(event) => setStorefrontOnly(event.target.checked)} />
                Storefront licences only
              </label>
              <label className="flex items-center gap-2 py-2 text-sm text-slate-700">
                <input type="checkbox" checked={pricedOnly} onChange={(event) => setPricedOnly(event.target.checked)} />
                Hide licences with no price
              </label>
              <button
                type="button"
                onClick={() => void save()}
                disabled={saving || !dirty}
                className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save prices'}
              </button>
            </div>
            {!storefrontOnly && (
              <p className="mt-2 text-xs text-slate-500">
                Government, education and Telstra-only licences are shown only to customers whose Microsoft 365 already has them.
              </p>
            )}

            <div className="mt-4 flex flex-wrap gap-2" role="tablist" aria-label="Distributor price lists">
              {withTelstraFirst(reference.distributors).map((distributor) => {
                const active = distributor.id === activeDistributor;
                const invalid = invalidCount(draft[distributor.id]);
                const sameNamed = sameNamedIn(draft[distributor.id]).size;
                return (
                  <button
                    key={distributor.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveDistributor(distributor.id)}
                    className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                      active ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {distributor.label}
                    <span className="ml-1.5 text-xs text-slate-500">{pricedCount(draft[distributor.id])} priced</span>
                    {distributor.id === defaultDistributor && <span className="ml-1.5 text-xs font-medium text-emerald-700">Default</span>}
                    {invalid > 0 && <span className="ml-1.5 text-xs font-medium text-rose-600">{invalid} to fix</span>}
                    {sameNamed > 0 && <span className="ml-1.5 text-xs font-medium text-amber-700">{sameNamed} share a name</span>}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {retired.length > 0 && (
          <p className="mt-3 max-w-3xl rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            You have prices saved for licences that can&apos;t be sold any more, so customers don&apos;t see them:{' '}
            {retired.join(', ')}. Save to remove them.
          </p>
        )}
        {activeSameNames.length > 0 && (
          <p className="mt-3 max-w-3xl rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Microsoft gives some licences the same name, and customers see only the name. Your {activeLabel} list prices more
            than one licence named {activeSameNames.map((name) => `“${name}”`).join(', ')}, so customers buying through{' '}
            {activeLabel} see the same product twice. Price only the one you sell.
          </p>
        )}
        {message && <p className="mt-3 text-sm text-emerald-700">{message}</p>}
        {saveError && (
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-rose-600">
            <span>{saveError}</span>
            {conflict && (
              <button
                type="button"
                onClick={() => void load()}
                className="rounded-md border border-rose-300 bg-white px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
              >
                Reload prices (discards your unsaved changes)
              </button>
            )}
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {loading && <p className="p-6 text-sm text-slate-500">Loading Microsoft licences…</p>}
        {!loading && reference && (
          <table className="min-w-full text-sm">
            <caption className="sr-only">{activeLabel} prices</caption>
            <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Licence</th>
                {reference.terms.map((term) => (
                  <th key={term.key} className="px-4 py-2 font-medium">{term.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((licence) => {
                const sameNamed = activeSameNamed.get(licence.skuId);
                return (
                <tr key={licence.skuId} className="border-t border-slate-100">
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      onClick={() => setOpenLicenceId(licence.skuId)}
                      className="text-left"
                    >
                      <div className="font-medium text-slate-900 hover:text-blue-700">{licence.name}</div>
                      <div className="font-mono text-[11px] text-slate-500">{licence.skuPartNumber}</div>
                    </button>
                    {sameNamed && (
                      <div className="mt-0.5 text-[11px] text-amber-700">
                        Same name as {sameNamed.map((other) => other.skuPartNumber).join(', ')}, also priced
                      </div>
                    )}
                  </td>
                  {reference.terms.map((term) => {
                    const text = activeList[licence.skuId]?.[term.key] ?? '';
                    const valid = parsePrice(text).valid;
                    return (
                      <td key={term.key} className="px-4 py-2">
                        <input
                          value={text}
                          onChange={(event) => updatePrice(licence.skuId, term.key, event.target.value)}
                          className={`w-28 rounded border px-2 py-1 ${valid ? 'border-slate-300' : 'border-rose-500 bg-rose-50'}`}
                          inputMode="decimal"
                          aria-invalid={!valid}
                          aria-label={`${licence.name} ${term.label} price from ${activeLabel}`}
                        />
                      </td>
                    );
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {!loading && reference && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3">
          <p className="text-xs text-slate-500">
            {visible.length === 0
              ? (pricedOnly && !query.trim() ? 'No licences have a price on this list.' : 'No licences match.')
              : `Showing ${pageStart + 1}–${pageStart + pageRows.length} of ${visible.length}.`}
            {' '}Save stores every price you&apos;ve entered, including ones on other pages.
          </p>
          {pageCount > 1 && (
            <nav className="flex items-center gap-1" aria-label="Licence pages">
              <button
                type="button"
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage === 0}
                className="rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                Previous
              </button>
              {Array.from({ length: pageCount }, (_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setPage(index)}
                  aria-current={index === currentPage ? 'page' : undefined}
                  className={`min-w-[2rem] rounded-md border px-2 py-1 text-sm ${
                    index === currentPage
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage === pageCount - 1}
                className="rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                Next
              </button>
            </nav>
          )}
        </div>
      )}
      {openLicence && reference && (
        <LicenceDetail
          licence={openLicence}
          description={descriptions[openLicence.skuId] ?? ''}
          prices={activeList[openLicence.skuId]}
          terms={reference.terms}
          onDescription={(text) => {
            setDescriptions((current) => ({ ...current, [openLicence.skuId]: text }));
            setDirty(true);
            setMessage(null);
          }}
          onClose={() => setOpenLicenceId(null)}
        />
      )}
    </div>
  );
}

function LicenceDetail({
  licence,
  description,
  prices,
  terms,
  onDescription,
  onClose,
}: {
  licence: MicrosoftLicence;
  description: string;
  prices: TermPriceText | undefined;
  terms: MicrosoftLicenceReference['terms'];
  onDescription: (text: string) => void;
  onClose: () => void;
}) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getCachedIcon(licence.image).then((url) => {
      if (!cancelled) setImageUrl(url);
    });
    return () => { cancelled = true; };
  }, [licence.image]);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <button type="button" className="absolute inset-0 bg-black/30" aria-label="Close licence" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-labelledby="licence-detail-title" className="relative z-10 w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
        <div className="flex items-start gap-4">
          {imageUrl ? (
            <img src={imageUrl} alt="" className="h-16 w-16 flex-shrink-0 object-contain" />
          ) : (
            <div className="h-16 w-16 flex-shrink-0 rounded bg-slate-100" />
          )}
          <div className="min-w-0 flex-1">
            <h3 id="licence-detail-title" className="text-base font-semibold text-slate-900">{licence.name}</h3>
            <p className="mt-0.5 font-mono text-xs text-slate-500">{licence.skuPartNumber}</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-800">Close</button>
        </div>
        <label className="mt-4 block text-sm font-medium text-slate-700" htmlFor="licence-description">Description</label>
        <textarea
          id="licence-description"
          value={description}
          onChange={(event) => onDescription(event.target.value)}
          rows={4}
          placeholder="What customers see for this licence"
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
        />
        <dl className="mt-4 grid grid-cols-1 gap-2 text-sm">
          {terms.map((term) => (
            <div key={term.key} className="flex items-center justify-between gap-3">
              <dt className="text-slate-500">{term.label}</dt>
              <dd className="font-medium text-slate-900">{prices?.[term.key]?.trim() ? `$${prices[term.key]}` : 'No price'}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-3 text-xs text-slate-500">Save prices stores this description with the Telstra list.</p>
      </div>
    </div>
  );
}
